import uniq from 'lodash/uniq';

export class GroupUtil {

    /**
     * Retrieves the group's ID.
     * 
     * @param { object } groupData - The data to read.
     * @returns { string }
     */
    static getId( groupData ) {
        return groupData?.node?.id;
    }

    /**
     * Retrieves the group's name.
     * 
     * @param { object } groupData - The data to read.
     * @returns { string }
     */
    static getName( groupData ) {
        return groupData?.node?.name;
    }

    /**
     * The list of all attribute names found on the provided groups.
     * 
     * @param { object } groupData - The data to read.
     * @returns { { name: string, value: string }[] }
     */
    static getAllAttributes( groupData ) {
        let attributeList = groupData?.node?.attributes || [];
        attributeList = uniq( attributeList.filter( ( attribute ) => attribute?.name ) );
        return attributeList.map( ( attribute ) => {
            return {
                name: attribute?.name,
                value: attribute?.value,
            };
        } );
    }

    /**
     * The list of all attribute names found on the provided groups.
     * 
     * @param { object } groupData - The data to read.
     * @param { string } attributeName - The attribute name to look for.
     * @returns { { name: string, value: string } }
     */
    static getAttribute( groupData, attributeName ) {
        let attributeList = groupData?.node?.attributes || [];
        attributeList = uniq( attributeList.filter( ( attribute ) => attribute?.name ) );
        const foundAttribute = attributeList.find( ( attribute ) => attribute?.name === attributeName );
        return ( !foundAttribute ) ? undefined : {
            name: foundAttribute?.name,
            value: foundAttribute?.value,
        };
    }

    /**
     * Returns a list of only the groups that contain the attribute name. If an attribute
     * value is provided, then only the groups that contain the attribute name and also
     * the provided value for that attribute will be returned.
     * 
     * @param { object[] } groupDataList - The data to read.
     * @param { string } attributeName - The name of the attribute to include in the resulting array.
     * @param { string } [attributeValue] - The value required for the attribute to be included in the resulting array (optional).
     * @returns { object[] }
     */
    static filterListByAttribute( groupDataList, attributeName, attributeValue = undefined ) {
        const groupList = groupDataList || [];
        return groupList.filter( ( groupNode ) => {
            const groupAttribute = this.getAttribute( groupNode, attributeName );
            const hasAttribute = Boolean( groupAttribute );
            const mustMatchValue = attributeValue !== undefined;

            if ( mustMatchValue && hasAttribute ) {
                return ( attributeValue === groupAttribute?.value );
            }
            else {
                return hasAttribute;
            }
        } );
    }

    /**
     * Returns a list of only the groups that do not contain the attribute name. If an
     * attribute value is provided, then only the groups that do not contain the attribute
     * name and also the provided value for that attribute will be returned.
     * 
     * @param { object[] } groupDataList - The data to read.
     * @param { string } attributeName - The name of the attribute to include in the resulting array.
     * @param { string } [attributeValue] - The value required for the attribute to be included in the resulting array (optional).
     * @returns { object[] }
     */
    static filterListByMissingAttribute( groupDataList, attributeName, attributeValue = undefined ) {
        const groupList = groupDataList || [];
        return groupList.filter( ( groupNode ) => {
            const groupAttribute = this.getAttribute( groupNode, attributeName );
            const hasAttribute = Boolean( groupAttribute );
            const mustNotMatchValue = attributeValue !== undefined;

            if ( mustNotMatchValue && !hasAttribute ) {
                return !( attributeValue === groupAttribute?.value );
            }
            else {
                return !hasAttribute;
            }
        } );
    }
    
}
